import {
  BaseEntity,
  CommsMedium,
  Identity,
  IdentityAttributes,
  Organization,
  ResourceTypes,
  SubscriptionStatus,
  VendorGroup,
} from '@vendorflow/types'
import { addMembersToOrgUsingPOST, removeMembersFromOrgUsingDELETE } from 'apis/Organizations.api'
import { forEach, keyBy } from 'lodash'
import {
  addVendorsToVendorGroupUsingPOST,
  removeVendorsFromVendorGroupUsingDELETE,
} from 'features/contact-groups/VendorGroup.api'
import { testRegex } from 'services/Utils.service'

export function createNewIdentity(): IdentityAttributes {
  return {
    department: '',
    firstName: '',
    lastName: '',
    displayName: '',
    emails: [],
    phones: [],
  }
}
export interface SubStatusByIdentity {
  [id: string]: {
    [key in CommsMedium]: SubscriptionStatus | null
  }
}

export async function handleOrgChangesForAlice(
  identityId: string,
  prev: Organization | null,
  update: Organization | null
) {
  const members: BaseEntity<ResourceTypes.IDENTITIES>[] = [{ id: identityId, type: ResourceTypes.IDENTITIES }]

  if (update?.id == prev?.id) {
    return
  }

  if (update?.id && !prev?.id) {
    try {
      await addMembersToOrgUsingPOST(update.id, members)
    } catch (e) {
      console.error(e)
      throw new Error('Unable to successfully add this vendor to the organization.')
    }
  } else if (!update?.id && prev?.id) {
    try {
      await removeMembersFromOrgUsingDELETE(prev.id, members)
    } catch (e) {
      console.error(e)
      throw new Error('Unable to successfully remove this vendor from the organization.')
    }
  } else {
    try {
      await removeMembersFromOrgUsingDELETE(prev!.id, members)
    } catch (e) {
      console.error(e)
      throw new Error('Unable to successfully remove this vendor from the organization.')
    }
    try {
      await addMembersToOrgUsingPOST(update!.id, members)
    } catch (e) {
      console.error(e)
      throw new Error('Unable to successfully add this vendor to the organization.')
    }
  }
}

export async function handleVendorGroupChangesForAlice(
  aliceId: string,
  prevGroups: VendorGroup[],
  updatedGroups: VendorGroup[]
) {
  const updatedGroupsMap = keyBy(updatedGroups, 'id')
  const currGroupsMap = keyBy(prevGroups, 'id')

  // adds -- ids that exist in updated map, but not in curr group map
  const adds: string[] = []
  forEach(updatedGroupsMap, (_: VendorGroup, id: string) => {
    if (!currGroupsMap[id]) {
      adds.push(id)
    }
  })
  // @ts-ignore
  const addResult: any[] = await Promise.allSettled(
    adds.map((id) => addVendorsToVendorGroupUsingPOST(id, [{ id: aliceId, type: ResourceTypes.IDENTITIES }]))
  )
  // deletes -- ids that exist in curr map, but not in updated map
  const deletes: string[] = []
  forEach(currGroupsMap, (_: VendorGroup, id: string) => {
    if (!updatedGroupsMap[id]) {
      deletes.push(id)
    }
  })
  // @ts-ignore
  const deleteResult: any[] = await Promise.allSettled(
    deletes.map((id) => removeVendorsFromVendorGroupUsingDELETE(id, [{ id: aliceId, type: ResourceTypes.IDENTITIES }]))
  )

  const addErrorGroups: string[] = []
  const deleteErrorGroups: string[] = []

  addResult.forEach((result: any, index: number) => {
    if (result.status === 'rejected') {
      addErrorGroups.push(updatedGroupsMap[adds[index]].name)
    }
  })
  deleteResult.forEach((result: any, index: number) => {
    if (result.status === 'rejected') {
      deleteErrorGroups.push(currGroupsMap[deletes[index]].name)
    }
  })

  if (addErrorGroups.length > 0 || deleteErrorGroups.length > 0) {
    throw new Error(buildVendorGroupChangesErrorMessage(addErrorGroups, deleteErrorGroups))
  }
}

function buildVendorGroupChangesErrorMessage(addErrorGroups: string[], deleteErrorGroups: string[]): string {
  let message = ''
  if (addErrorGroups.length > 0) {
    message += 'Failed to add the vendor to the following groups: '
    addErrorGroups.forEach((name, index) => (message += index < addErrorGroups.length - 1 ? `${name}, ` : `${name}.\n`))
  }

  if (deleteErrorGroups.length > 0) {
    message += 'Failed to remove the vendor from the following groups: '
    deleteErrorGroups.forEach(
      (name, index) => (message += index < deleteErrorGroups.length - 1 ? `${name}, ` : `${name}.`)
    )
  }

  return message
}

function isValidAlphaChar(letter: string): boolean {
  return testRegex(new RegExp('[a-zA-Z]'), letter).isValid
}

export function getDisplayNameFromIdentity({ displayName, firstName, lastName, emails, phones, id }: Identity): string {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim()
  return displayName && displayName.trim().length > 0
    ? displayName.trim()
    : fullName
    ? fullName
    : emails.length > 0
    ? emails[0]
    : phones.length > 0
    ? phones[0].number
    : id!
}

export function getSearchableNameFromIdentity({ displayName, firstName, lastName }: Identity): string {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim()
  return `${displayName.trim()} ${fullName}`
}

export function getInitialsFromIdentity(identity: Identity): string {
  const displayName = getDisplayNameFromIdentity(identity)
  const parts = (displayName || '').split(/\s+/)
  if (parts.length > 1) {
    const firstLetter = isValidAlphaChar(parts[0][0]) ? parts[0][0] : ''
    const secondLetter = isValidAlphaChar(parts[1][0]) ? parts[1][0] : ''
    return `${firstLetter}${secondLetter}`.toUpperCase()
  }
  if (parts.length === 1 && parts[0].length > 0) {
    return isValidAlphaChar(parts[0][0]) ? parts[0][0].toUpperCase() : ''
  }

  return ''
}
